import SoftooMap from "../Map";
import Map from "ol/Map";
import { Point, MultiPoint, LineString,
         Polygon, MultiLineString, MultiPolygon } from "ol/geom";
import LinearRing from "ol/geom/LinearRing";
import GeometryCollection from "ol/geom/GeometryCollection";

import Feature from "ol/Feature";
import { altKeyOnly, singleClick, click } from 'ol/events/condition.js';
import { Select, Draw, Modify } from "ol/interaction";
import { L } from "@/common/filters";

import * as jsts from "jsts";

export class ConvexHull extends Select {
  private $map: SoftooMap;
  private map: Map;
  private jstsOL3Parser;

  private select:Select;
  private draw:Draw;
  private modify:Modify;
  private polygonSource;
  private drawSource;
  private drawStyle;
  private pointLayers;
  private thePolygon:Feature;
  
  constructor(options) {
    options = options || {};
    let polygonSource;
    if (options.polygonSource) {
      polygonSource = options.polygonSource;
      delete options.polygonSource;
    }
    
    let drawStyle, drawSource;
    if (options.drawStyle) {
      drawStyle = options.drawStyle;
      delete options.drawStyle;
    }
    if (options.drawSource) {
      drawSource = options.drawSource;
      delete options.drawSource;
    }
    let pixelTolerance = 6;
    if (options.pixelTolerance) {
      pixelTolerance = options.pixelTolerance; //for modify
      delete options.pixelTolerance;
    }
    let clickTolerance = Math.max(pixelTolerance-2, 4);

    super(Object.assign({
      wrapX: false,
      condition: () => {
        return false; // force to use draw polygon to select points
      }
    }, options));

    this.select = <Select>this;
    this.pointLayers = options.layers;

    this.drawSource = drawSource;
    this.drawStyle = drawStyle;
    this.polygonSource = polygonSource;

    options = {
      style: drawStyle,
      source: drawSource
    }
    this.draw = new Draw(Object.assign({}, options, {
      type: 'Polygon',
      clickTolerance: clickTolerance
    }));
    this.modify = new Modify(Object.assign({}, options, {
      pixelTolerance: pixelTolerance,
      deleteCondition: (event) => {
        return altKeyOnly(event) && (singleClick(event) || click(event));
      }
    }));
    this.startDrawPolygon_ = (event) => {
      this.startDrawPolygon(event);
    }
    this.endDrawPolygon_ = (event) => {
      this.endDrawPolygon(event);
    }
    this.modifyDrawPolygon_ = (event) => {
      this.modifyDrawPolygon(event);
    }

    this.jstsOL3Parser = new jsts.io.OL3Parser();
    this.jstsOL3Parser.inject(Point, LineString, LinearRing, Polygon, MultiPoint, MultiLineString, MultiPolygon, GeometryCollection);
  }

  private startDrawPolygon_;
  startDrawPolygon(_event) {
    this.drawSource.clear();
  }

  private endDrawPolygon_;
  endDrawPolygon(event) {
    if (this.pointLayers) {
      this.createConvexHull(event.feature);
    }
  }

  private modifyDrawPolygon_;
  modifyDrawPolygon(event) {
    this.endDrawPolygon(Object.assign(event.mapBrowserEvent, {
      feature: event.features.item(0)
    }));
  }

  createConvexHull(feature:Feature) {
    let polygon = feature.getGeometry();
    let selected:Feature[] = [];
    this.pointLayers.forEach((layer) => {
      let features = layer.getSource().getFeatures();
      if (features) {
        features.forEach((feature) => {
          let g = feature.getGeometry();
          if (feature.get('STATE') != 'Delete' && g instanceof Point) {
            // NOTE: point on polygon boundary is not intersected
            if (polygon.intersectsCoordinate(g.getCoordinates())) {
              selected.push(feature);
            }
          }
        });
      }
    });
    
    let selectedSource = this.select.getOverlay().getSource();
    selectedSource.clear();
    selectedSource.addFeatures(selected);
    if (selected.length>=3) {
      let coords = selected.map((point) => {
        return (<Point>point.getGeometry()).getCoordinates();
      });
      let multiPoints = new MultiPoint(coords);
      let jstsPoints = this.jstsOL3Parser.read(multiPoints);
      let convexHull = jstsPoints.convexHull();
      let olPolygon = this.jstsOL3Parser.write(convexHull);
      //jsts does not have concaveHull
      //let concaveHull = jstsPoints.concaveHull();
      //let olPolygon = this.jstsOL3Parser.write(concaveHull);
      //GISagro farmland is Polygon not MultiPolygon
      //if (olPolygon instanceof Polygon) {
      //  olPolygon = new MultiPolygon([olPolygon.getCoordinates()]);
      //}
      let isNew;
      if (!this.thePolygon) {
        this.thePolygon = new Feature();
        isNew = true;
      }
      this.thePolygon.setGeometry(olPolygon);
      let areaSqm = this.$map.calcArea(olPolygon);
      this.thePolygon.set('area_sqm',areaSqm);
      if (isNew) {
        this.polygonSource.addFeature(this.thePolygon);
        this.$map.addUndo(this.thePolygon, 'Insert');
      }
      
      /* prompt for acception
      let centroid = this.$map.getFeatureCentroid(feature).map;
      this.$map.showInfoPopup(L('attention'), L('sAreYouGoingToDeleteSelectedFeatures'), {
        position: centroid,
        offset: [0, -20],
        okText: L('yes'),
        cancelText: L('no'),
        action: (answer) => {
          if (answer=='accept') {
            this.deleteFeaturesInPolygon(selected);
          }
          this.$map.hideInfoPopup();
        },
        onClose: () => {
          this.clearFeaturesInPolygon();
        }
      });*/
    } else {
      this.$map.$emit('error', {
        message: L('sAtLeast3PointsAreRequired')
      });
    }
  }

  setMap(map:Map) {
    if (map) {
      this.$map = map['SOFTOO_MAP'];
      this.map = map;
      this.draw.on('drawstart', this.startDrawPolygon_);
      this.draw.on('drawend', this.endDrawPolygon_);
      this.modify.on('modifyend', this.modifyDrawPolygon_);
      this.$map.addInteractions([this.draw,this.modify]);
      //this.map.addInteraction(this.draw);
      //this.map.addInteraction(this.modify);
      //this.$map.createSnap();
      super.setMap(map);
    }
  }

  remove() {
    if (this.map) {
      this.jstsOL3Parser = null;
      this.drawSource.clear();
      this.select.getOverlay().getSource().clear();
      //this.$map.hideInfoPopup();
      this.draw.un('drawstart', this.startDrawPolygon_);
      this.draw.un('drawend', this.endDrawPolygon_);
      this.modify.un('modifyend', this.modifyDrawPolygon_);
      this.map.removeInteraction(this.draw);
      this.map.removeInteraction(this.modify);
      this.map.removeInteraction(this);
    }
  }
}